// ── OWNER DRAWS ───────────────────────────────────────────────────────────────
// Mounts under Procurement Management → Owner's Draws.
// Read by owner + bookkeeper, write by owner only.
// Maps to QB as a Journal Entry: debit cash, credit Owner's Draw equity.

import React, { useState, useMemo } from 'react';
import { useAppContext } from './AppContext';
import { calcOwnerDrawsTotal, calcOwnerDrawsYearToDate, fmtMoney } from '../utils';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    var s = String(iso);
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    var d = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return String(iso).slice(0, 10); }
}

var inp = {
  width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit',
  border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff',
  outline: 'none', boxSizing: 'border-box',
};
var lbl = { fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 4, display: 'block' };
var btnGhost = { height: 32, padding: '0 14px', borderRadius: 18, border: '0.5px solid #E0E0DC', background: '#fff', color: '#111', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };
var btnPrimary = { height: 32, padding: '0 16px', borderRadius: 18, border: 'none', background: '#111', color: '#fff', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };
var btnDanger = { height: 26, padding: '0 12px', borderRadius: 14, border: '0.5px solid #C8322A', background: '#FFFFFF', color: '#C8322A', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };

var CATEGORIES = ['Personal', 'Tax payment', 'Operating', 'Other'];

export default function OwnerDrawsSection() {
  var ctx = useAppContext();
  var ownerDraws = ctx.ownerDraws || [];
  var saveOwnerDraw = ctx.saveOwnerDraw;
  var deleteOwnerDraw = ctx.deleteOwnerDraw;
  var userEmail = ctx.userEmail || '';
  var isOwner = ctx.isOwner;

  var ams = useState(false);                                     var addOpen = ams[0];     var setAddOpen = ams[1];
  var es  = useState(null);                                      var editing = es[0];      var setEditing = es[1];
  var ds  = useState(function(){return new Date().toISOString().slice(0,10);});
  var drawDate = ds[0];   var setDrawDate = ds[1];
  var as  = useState('');                                        var amount = as[0];       var setAmount = as[1];
  var cs  = useState('Personal');                                var category = cs[0];     var setCategory = cs[1];
  var rs  = useState('');                                        var reference = rs[0];    var setReference = rs[1];
  var ns  = useState('');                                        var notes = ns[0];        var setNotes = ns[1];
  var errS = useState('');                                       var err = errS[0];        var setErr = errS[1];

  var sorted = useMemo(function() {
    return ownerDraws.slice().sort(function(a, b) {
      return String(b.drawDate || '').localeCompare(String(a.drawDate || ''));
    });
  }, [ownerDraws]);

  var ytdTotal = calcOwnerDrawsYearToDate(ownerDraws);
  var allTimeTotal = calcOwnerDrawsTotal(ownerDraws);
  var thisYear = new Date().getFullYear();

  function openAdd() {
    setEditing(null);
    setDrawDate(new Date().toISOString().slice(0, 10));
    setAmount('');
    setCategory('Personal');
    setReference('');
    setNotes('');
    setErr('');
    setAddOpen(true);
  }

  function openEdit(draw) {
    setEditing(draw);
    setDrawDate(draw.drawDate || new Date().toISOString().slice(0, 10));
    setAmount(String(draw.amount || ''));
    setCategory(draw.category || 'Personal');
    setReference(draw.reference || '');
    setNotes(draw.notes || '');
    setErr('');
    setAddOpen(true);
  }

  function submit() {
    setErr('');
    var cleaned = String(amount).replace(/[$,\s]/g, '');
    var amt = parseFloat(cleaned);
    if (!amt || isNaN(amt) || amt <= 0) { setErr('Enter a valid positive amount.'); return; }
    var base = editing ? Object.assign({}, editing) : { createdBy: userEmail };
    saveOwnerDraw(Object.assign(base, {
      drawDate: drawDate,
      amount: amt,
      category: category,
      reference: reference,
      notes: notes,
    }));
    setAddOpen(false);
  }

  async function removeDraw(draw) {
    if (!window.confirm('Delete this draw of ' + fmtMoney(draw.amount) + '?')) return;
    var result = await deleteOwnerDraw(draw.id);
    if (!result || !result.ok) {
      window.alert('Cannot delete:\n\n' + (result && result.error || 'Unknown error'));
    }
  }

  return (
    <div style={{ padding: '20px 24px 40px', maxWidth: 1000, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>Owner's Draws</div>
          <div style={{ fontSize: 12, color: '#888' }}>History of personal draws from the business.</div>
        </div>
        <div style={{ flex: 1 }} />
        {isOwner && <button style={btnPrimary} onClick={openAdd}>+ Record Draw</button>}
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 22 }}>
        <Stat label={thisYear + ' year-to-date'} value={fmtMoney(ytdTotal)} />
        <Stat label="All-time total" value={fmtMoney(allTimeTotal)} />
        <Stat label="Entries" value={String(ownerDraws.length)} />
      </div>

      {/* Ledger */}
      <div style={{ background: '#fff', border: '0.5px solid #E0E0DC', borderRadius: 10, overflow: 'hidden' }}>
        {sorted.length === 0 ? (
          <div style={{ padding: '50px 20px', color: '#888', fontSize: 13, fontStyle: 'italic', textAlign: 'center' }}>
            No draws recorded yet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#FAFAF8', borderBottom: '0.5px solid #E0E0DC' }}>
                <th style={th}>Date</th>
                <th style={th}>Category</th>
                <th style={th}>Reference</th>
                <th style={Object.assign({}, th, { textAlign: 'right' })}>Amount</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(function(d) {
                var clickable = isOwner;
                return (
                  <tr key={d.id}
                    onClick={clickable ? function() { openEdit(d); } : undefined}
                    style={{ cursor: clickable ? 'pointer' : 'default', borderBottom: '0.5px solid #EFEFEC' }}
                    onMouseEnter={clickable ? function(e) { e.currentTarget.style.background = '#FAFAF8'; } : undefined}
                    onMouseLeave={clickable ? function(e) { e.currentTarget.style.background = '#FFFFFF'; } : undefined}>
                    <td style={td}>{fmtDate(d.drawDate)}</td>
                    <td style={td}>{d.category || '—'}</td>
                    <td style={Object.assign({}, td, { color: '#555' })}>
                      <div>{d.reference || '—'}</div>
                      {d.notes && <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{d.notes}</div>}
                    </td>
                    <td style={Object.assign({}, td, { textAlign: 'right', fontWeight: 500, color: '#111' })}>{fmtMoney(d.amount)}</td>
                    <td style={Object.assign({}, td, { textAlign: 'right', width: 90 })}>
                      {isOwner && (
                        <button onClick={function(e) { e.stopPropagation(); removeDraw(d); }} style={btnDanger}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {addOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 480, maxWidth: '94vw', background: '#fff', borderRadius: 10, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 16 }}>
              {editing ? 'Edit Draw' : 'Record Owner\'s Draw'}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={lbl}>Date</label>
                <input type="date" value={drawDate} onChange={function(e) { setDrawDate(e.target.value); }} style={inp} />
              </div>
              <div>
                <label style={lbl}>Amount</label>
                <input type="text" inputMode="decimal" placeholder="0.00" value={amount} onChange={function(e) { setAmount(e.target.value); }} style={inp} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Category</label>
              <select value={category} onChange={function(e) { setCategory(e.target.value); }} style={inp}>
                {CATEGORIES.map(function(c) { return <option key={c} value={c}>{c}</option>; })}
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Reference</label>
              <input placeholder="check #, transfer ref, etc." value={reference} onChange={function(e) { setReference(e.target.value); }} style={inp} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>Notes</label>
              <textarea value={notes} onChange={function(e) { setNotes(e.target.value); }} rows={2}
                style={Object.assign({}, inp, { resize: 'vertical', minHeight: 60 })} />
            </div>

            {err && <div style={{ marginBottom: 12, padding: '8px 12px', background: '#FDECEA', border: '0.5px solid #F2A39C', color: '#C8322A', fontSize: 11, borderRadius: 6 }}>{err}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={function() { setAddOpen(false); }} style={btnGhost}>Cancel</button>
              <button onClick={submit} style={btnPrimary}>Save Draw</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

var th = { padding: '11px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' };
var td = { padding: '12px 14px', fontSize: 12, color: '#444', whiteSpace: 'nowrap' };

function Stat(p) {
  return (
    <div style={{ background: '#fff', border: '0.5px solid #E0E0DC', borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 4 }}>{p.label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: '#111' }}>{p.value}</div>
    </div>
  );
}
